import React from 'react';
import api from '../services/api';

export default function CommitteeMeetingRSVP() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [invite, setInvite] = React.useState(null);
  const [status, setStatus] = React.useState('');

  const token = React.useMemo(() => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('token') || '';
    } catch { return ''; }
  }, []);

  const fetchInvite = async () => {
    if (!token) { setError('Missing token.'); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/committeeMeetings/invite/${token}`);
      if (res.data?.success) {
        setInvite(res.data.invite);
        setStatus(res.data.invite.STATUS || '');
      } else {
        setError(res.data?.error || 'Failed to load invite');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load invite');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchInvite(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Generate ICS calendar content (1 hour duration default) and trigger download
  const generateAndDownloadICS = (inviteData) => {
    if (!inviteData) return;
    try {
      const dateStr = inviteData.MEETING_DATE; // YYYY-MM-DD
      const timeStr = (inviteData.MEETING_TIME || '').slice(0,5); // HH:MM
      if(!dateStr || !timeStr) return;
      // Construct local date objects
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hour, minute] = timeStr.split(':').map(Number);
      const start = new Date(year, month - 1, day, hour, minute, 0);
      const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hour

      const toICSDate = (d) => {
        const pad = (n) => n.toString().padStart(2, '0');
        return `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
      };

      const uid = `${inviteData.MEETING_ID || token}@clocktower`; // MEETING_ID included if present
      const dtstamp = toICSDate(new Date());
      const dtstart = toICSDate(start);
      const dtend = toICSDate(end);
      const summary = 'Health & Safety Committee Meeting';
      const location = (inviteData.LOCATION || '').replace(/\n/g, ' ');
      const description = 'Committee Meeting. This event was generated after you RSVP\nDo not reply.';

      const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Clocktower//CommitteeMeetings//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${dtstamp}`,
        `DTSTART:${dtstart}`,
        `DTEND:${dtend}`,
        `SUMMARY:${summary}`,
        `LOCATION:${location}`,
        'DESCRIPTION:' + description.replace(/\r?\n/g, '\n'),
        'END:VEVENT',
        'END:VCALENDAR'
      ];
      const icsContent = lines.join('\r\n');
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `committee-meeting-${dateStr}.ics`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
        // Attempt to close the tab (might only work if it was opened from the email as a new tab)
        try { window.close(); } catch(_) { /* ignore */ }
      }, 600);
    } catch(e){
      console.error('Failed to generate ICS:', e);
    }
  };

  const submitRsvp = async (next) => {
    try {
      await api.post(`/api/committeeMeetings/invite/${token}/rsvp`, { status: next });
      setStatus(next);
      // If attending, immediately generate the ICS download
      if(next === 'attending') {
        generateAndDownloadICS(invite);
      }
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Failed to update RSVP');
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (error) return <div style={{ padding: 16, color: '#b91c1c' }}>Error: {error}</div>;
  if (!invite) return <div style={{ padding: 16 }}>Not found.</div>;

  const meetingTitle = `Health & Safety Committee Meeting`;
  const when = `${invite.MEETING_DATE || ''} ${invite.MEETING_TIME || ''}`;

  return (
    <div style={{ maxWidth: 720, margin: '24px auto', padding: 16, fontFamily: 'Arial, sans-serif' }}>
      <h2 style={{ marginTop: 0 }}>{meetingTitle}</h2>
      <p><strong>Date/Time:</strong> {when}</p>
      <p><strong>Location:</strong> {invite.LOCATION || ''}</p>
      {invite.AGENDA_PDF_URL ? (
        <p><strong>Agenda:</strong> <a href={invite.AGENDA_PDF_URL} target="_blank" rel="noreferrer">Open PDF</a></p>
      ) : null}
      <hr />
      <p>Hello {invite.MEMBER_NAME || invite.MEMBER_EMAIL}, please select your attendance:</p>

      <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => submitRsvp('attending')}
          disabled={status === 'attending'}
          style={{ padding: '8px 12px', background: status === 'attending' ? '#10b981' : '#e5e7eb', border: 0, borderRadius: 6 }}
        >
          Attending
        </button>
        <button
          onClick={() => submitRsvp('regrets')}
          disabled={status === 'regrets'}
          style={{ padding: '8px 12px', background: status === 'regrets' ? '#ef4444' : '#e5e7eb', color: status === 'regrets' ? '#fff' : '#111827', border: 0, borderRadius: 6 }}
        >
          Regrets
        </button>
        {status === 'attending' && (
          <button
            onClick={() => generateAndDownloadICS(invite)}
            style={{ padding: '8px 12px', background: '#2563eb', color:'#fff', border:0, borderRadius:6 }}
          >
            Add to Calendar (.ics)
          </button>
        )}
      </div>
      {status ? (
        <div style={{ marginTop: 12 }}>
          <p>Your current response: <strong style={{ textTransform: 'capitalize' }}>{status}</strong></p>
          {status === 'attending' && <p style={{ fontSize:'12px', color:'#374151' }}>An .ics calendar file was downloaded. If the tab does not close automatically you may close it manually.</p>}
        </div>
      ) : (
        <p style={{ marginTop: 12, color: '#6b7280' }}>No response recorded yet.</p>
      )}
    </div>
  );
}
